/**
 * UserLocationMarker
 *
 * The local user's own live-location dot (Google-Maps style): a fixed-size
 * theme-accent dot with a white/dark ring, a pulsing presence halo behind it,
 * and an optional heading cone rotated to the device heading. Rendered inside a
 * MapLibre `<Marker>` whose container carries the shared z-index class.
 *
 * @module components/map/UserLocationLayer/parts/UserLocationMarker
 */
import type { CSSProperties } from 'react';
import { memo } from 'react';
import { Marker } from '@vis.gl/react-maplibre';
import { Tooltip } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import {
  USER_LOCATION_DOT_SIZE_PX,
  USER_LOCATION_HALO_SIZE_PX,
  USER_LOCATION_HALO_PULSE_SCALE,
  USER_LOCATION_PULSE_DURATION_S,
  USER_LOCATION_PULSE_OPACITY_MAX,
  USER_LOCATION_PULSE_OPACITY_MIN,
} from '@/constants';
import type { MemberLocation } from '@/types';

import { HeadingCone } from './HeadingCone';
import styles from './UserLocationMarker.module.css';

export interface UserLocationMarkerProps {
  /** The local user's current position (drives the dot). */
  location: MemberLocation;
}

/**
 * Static design tokens injected as CSS custom properties so the keyframe
 * timing/scale/opacity and dot sizing stay sourced from `@/constants`
 * (no magic numbers in CSS). These never change at runtime.
 */
const MARKER_TOKENS: CSSProperties = {
  '--dot-size': `${USER_LOCATION_DOT_SIZE_PX}px`,
  '--halo-size': `${USER_LOCATION_HALO_SIZE_PX}px`,
  '--halo-pulse-scale': USER_LOCATION_HALO_PULSE_SCALE,
  '--pulse-duration': `${USER_LOCATION_PULSE_DURATION_S}s`,
  '--pulse-opacity-max': USER_LOCATION_PULSE_OPACITY_MAX,
  '--pulse-opacity-min': USER_LOCATION_PULSE_OPACITY_MIN,
} as CSSProperties;

/**
 * Renders the current user's own position marker. Heading cone is only shown
 * when a numeric heading is available on the location.
 */
export const UserLocationMarker = memo(function UserLocationMarker({
  location,
}: UserLocationMarkerProps) {
  const { t } = useTranslation();

  return (
    <Marker
      longitude={location.lng}
      latitude={location.lat}
      className="user-location-marker-container"
    >
      <Tooltip label={t('map.userLocation.youTooltip')} withArrow>
        <div className={styles.marker} style={MARKER_TOKENS}>
          <span className={styles.halo} aria-hidden="true" />
          <HeadingCone
            heading={location.heading}
            color="var(--mantine-primary-color-filled)"
          />
          <span className={styles.dot} aria-hidden="true" />
        </div>
      </Tooltip>
    </Marker>
  );
});
